import { datadogRum } from "@datadog/browser-rum";
import { Identity, useIdentity } from "@equipmentshare/auth0-react";
import { useHeaper } from "@equipmentshare/heaper";
import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

import "@equipmentshare/ds2/dist/assets/ds2.css";
import { EuiProvider, THEME_TOKENS } from "@equipmentshare/ds2";

import { QueryClientWrapper } from "../queryClientWrapper";

import { Company } from "~/Company";
import * as Context from "~/Context";
import * as Document from "~/Document";
import * as Driver from "~/Driver";
import * as ELD from "~/ELD";
import { Graph } from "~/Graph";
import { LaunchDarkly } from '~/launchDarkly'
import * as Messaging from "~/Messaging";
import { Remote } from "~/Remote";
import { State } from "~/State";
import { getLaunchdarklyClientId } from '~/utils'
import { WebAPI } from "~/WebAPI";
import { showElogsBrandedConsoleInfo } from "./Console";
import { AppRouter } from "./AppRouter";
import { formatRumUser } from "@equipmentshare/audit-logger-ui";

const INTERCOM_APP_ID =
  window.location.href.includes("app") &&
  !window.location.href.includes("staging")
    ? "cc3wvy5y"
    : "n3j6c2wc";

const datadogEnvironment =
  window.location.href.includes("app") &&
  !window.location.href.includes("staging")
    ? "prod"
    : "stage";

const isStagingOrProduction = window.location.href.includes("app")

const { init, startSessionReplayRecording } = datadogRum
const version = process.env.SEMCOUNTER_VERSION
const release = `elogs-web@${version}`

if(isStagingOrProduction){
  init( {
    applicationId: '029d4beb-91c6-4a05-b1ca-75387ce3f102',
    clientToken: 'pub6843ced6eb8e3239aa2fd3e6fd7c399a',
    site: 'datadoghq.com',
    service: 'elogs-web',
    env: datadogEnvironment,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'allow',
    allowedTracingUrls: [ /^https:\/\/(?![^.]*auth[^.]*\.)[^/]+\.(estrack|equipmentshare)\.com/i ],
  })
  startSessionReplayRecording()
  showElogsBrandedConsoleInfo( `Datadog loaded: { release: ${release}, environment: ${datadogEnvironment}}` )
}

export function App({
  company,
  identity,
}: {
  readonly company: Company;
  readonly identity: Identity;
}) {
  const store = React.useMemo(() => App.store(company, identity), []);
  App.useInitialEffects(store);
  const rumUser = formatRumUser(identity);

  // Datadog identification
  useEffect(() => {
    datadogRum.setUser(rumUser);
  }, [identity]);

  return (
    <QueryClientWrapper>
      <LaunchDarkly clientId={getLaunchdarklyClientId()}>
        <EuiProvider modify={THEME_TOKENS}>
          <IntercomProvider appId={INTERCOM_APP_ID}>
            <Remote.Provider>
              <HeaperComponent />
              <State.Provider store={store}>
                <Graph.Provider>
                  <WebAPI.Provider>
                    <BrowserRouter
                      //  TODO: Remove after beta
                      basename={
                        window.location.host.includes("beta") ? "/" : "/e-logs"
                      }
                    >
                      <AppRouter />
                    </BrowserRouter>
                  </WebAPI.Provider>
                </Graph.Provider>
              </State.Provider>
            </Remote.Provider>
          </IntercomProvider>
        </EuiProvider>
      </LaunchDarkly>
    </QueryClientWrapper>
  );
}

export const HeaperComponent = () => {
  const { accessToken, idToken }: Identity = useIdentity();
  useHeaper(
    "E-logs",
    Context.isProductionEnvironment()
      ? "Production"
      : window.location.hostname.includes("localhost")
      ? "Sandbox"
      : "Staging",
    {
      accessToken,
      idToken,
      extendEventProps: () => ({ app_name: "E-logs Web" }),
    }
  );
  return null;
};

export namespace App {
  export const store = (company: Company, identity: Identity) =>
    State.Store.create(company, Context.initial(identity));

  export const useInitialEffects = (store: ReturnType<typeof App.store>) =>
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dispatch = store.dispatch as any;

      /* eslint-disable functional/no-expression-statement */
      dispatch(Driver.Action.Requested.create());
      dispatch(ELD.Action.Requested.create());
      dispatch(Document.Action.Requested.create());
      dispatch(Messaging.Summary.Action.Requested.create());
      /* eslint-enable functional/no-expression-statement */
    }, []);
}
